import { ScreenType } from '@tmdb-graphql-api/schema/types';
import { format } from 'date-fns';
import { castArray } from 'lodash-es';
import { type Mark } from '#components/Form/RangeSlider/types.ts';
import { type CompanyOption } from '../CompanyMultiSelectDropdown/types.ts';
import { type Chip, type MovieFormikValues, type Option, type TvFormikValues } from '../types.ts';
import { swapValuesWithLabels } from './swapValuesWithLabels.ts';

const baseTransformers = {
  companies: (value: Option[]) => value.map(({ id }) => ({ id, label: '' })),
  runtimes: (values: number[]) => ({
    id: 'runtimes',
    label: `Runtime: ${String(values[0])} to ${String(values[1])} hrs`,
  }),
  voteAverages: (values: number[]) => ({
    id: 'voteAverage',
    label: `Vote Average: ${String(values[0])} to ${String(values[1])}`,
  }),
  withGenres: (value: Option[], { genres }: { genres: Option[] }) =>
    value.map(({ id, name }) => {
      // In the wild, name can actually be undefined
      // eslint-disable-next-line @typescript-eslint/no-unnecessary-condition
      const finalName = name ?? genres.find(entry => entry.id === id)?.name;
      return { id, label: `With Genre: ${finalName}` };
    }),
  withKeywords: (value: Option[], { keywords }: { keywords: Option[] }) =>
    value.map(({ id, name }) => {
      // In the wild, name can actually be undefined
      // eslint-disable-next-line @typescript-eslint/no-unnecessary-condition
      const finalName = name ?? keywords.find(entry => entry.id === id)?.name;
      return { id, label: `With Keyword: ${finalName}` };
    }),
  withoutGenres: (value: Option[], { genres }: { genres: Option[] }) =>
    value.map(({ id, name }) => {
      // In the wild, name can actually be undefined
      // eslint-disable-next-line @typescript-eslint/no-unnecessary-condition
      const finalName = name ?? genres.find(entry => entry.id === id)?.name;
      return { id, label: `Without Genre: ${finalName}` };
    }),
  withoutKeywords: (value: Option[], { keywords }: { keywords: Option[] }) =>
    value.map(({ id, name }) => {
      // In the wild, name can actually be undefined
      // eslint-disable-next-line @typescript-eslint/no-unnecessary-condition
      const finalName = name ?? keywords.find(entry => entry.id === id)?.name;
      return { id, label: `Without Keyword: ${finalName}` };
    }),
};

const movieTransformers = {
  cast: (value: Option[]) => value.map(({ id }) => ({ id, label: '' })),
  certifications: (values: number[], filterOptions: Record<string, Mark[] | Option[]>) => {
    // Has to be Mark[] at this point
    // eslint-disable-next-line @typescript-eslint/consistent-type-assertions
    const labels = swapValuesWithLabels(values, filterOptions.certifications as Mark[]);

    if (labels[0] === labels[1]) {
      return {
        id: 'certifications',
        label: `Cert: ${String(labels[0])}`,
      };
    }

    return {
      id: 'certifications',
      label: `Cert: ${String(labels[0])} to ${String(labels[1])}`,
    };
  },
  crew: (value: Option[]) => value.map(({ id }) => ({ id, label: '' })),
  releaseType: (value: Option[]) => value.map(({ id, name }) => ({ id, label: `Release type: ${name}` })),
  releaseYears: (value: number[]) => {
    if (value[0] === value[1]) {
      return {
        id: 'releaseYears',
        // The array cannot be undefined at this point
        // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
        label: `Release year: ${format(new Date(value[0]!, 1, 1), 'y')}`,
      };
    }

    return {
      id: 'releaseYears',
      // The array cannot be undefined at this point
      // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
      label: `Release year: ${format(new Date(value[0]!, 1, 1), 'y')} to ${format(new Date(value[1]!, 1, 1), 'y')}`,
    };
  },
  ...baseTransformers,
};

const tvTransformers = {
  firstAired: (value: number[]) => {
    if (value[0] === value[1]) {
      return {
        id: 'firstAired',
        // The array cannot be undefined at this point
        // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
        label: `First aired: ${format(new Date(value[0]!, 1, 1), 'y')}`,
      };
    }

    return {
      id: 'firstAired',
      // The array cannot be undefined at this point
      // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
      label: `First aired: ${format(new Date(value[0]!, 1, 1), 'y')} to ${format(new Date(value[1]!, 1, 1), 'y')}`,
    };
  },
  screenedTheatrically: () => ({ id: 'screenedTheatrically', label: 'Screened theatrically' }),
  status: (value: Option[]) => value.map(({ id, name }) => ({ id, label: `Status: ${name}` })),
  type: (value: Option[]) => value.map(({ id, name }) => ({ id, label: `Type: ${name}` })),
  ...baseTransformers,
};

const isTvValue = (
  _key: keyof MovieFormikValues | keyof TvFormikValues,
  type: ScreenType,
): _key is keyof TvFormikValues => type === ScreenType.Tv;

export const transformValueIntoChips = (
  key: keyof MovieFormikValues | keyof TvFormikValues,
  value: Option[] | CompanyOption[] | number[] | undefined,
  filterOptions: Record<string, Mark[] | Option[]>,
  type: ScreenType,
): Chip[] => {
  return castArray(
    // @ts-expect-error Need to come back and fix
    isTvValue(key, type) ? tvTransformers[key](value, filterOptions) : movieTransformers[key](value, filterOptions),
  );
};
